import { useState } from "react";
import { Check } from "lucide-react";

// TAP FILTERS (Search S5): every choice is a tap, never typing, and shows how many it leaves BEFORE you tap it —
// counted with every other filter already on. A choice that would leave 0 is shown greyed with "0" and can't be
// tapped (his call 1). Tap a picked one again to drop it.
export function TapChips({ testid, options, value, onPick, max = 8 }) {
  const [all, setAll] = useState(false);
  const list = options || [];
  const shown = all ? list : list.filter((o, i) => i < max || o.value === value);
  const more = list.length - shown.length;
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1.5">
      {shown.map((o) => {
        const on = o.value === value;
        const empty = !on && !o.count;
        return (
          <button key={o.value} type="button" data-testid={`${testid}-opt-${slug(o.value)}`} disabled={empty}
            aria-pressed={on} onClick={() => onPick(on ? "" : o.value)}
            className={`inline-flex items-center gap-1.5 min-h-[44px] rounded-full border px-3.5 text-sm font-medium transition-colors ${
              on ? "border-[#E0781A] bg-[#E0781A] text-white"
                : empty ? "border-slate-200 bg-slate-50 text-slate-300 cursor-not-allowed"
                  : "border-slate-300 bg-white text-slate-700 hover:border-[#E0781A] hover:text-[#B45309]"}`}>
            {on && <Check className="w-3.5 h-3.5" />}
            <span className="max-w-[12rem] truncate">{o.label}</span>
            <span data-testid={`${testid}-count-${slug(o.value)}`}
              className={`font-mono text-xs rounded px-1.5 py-0.5 ${on ? "bg-white/25 text-white" : empty ? "text-slate-300" : "bg-slate-100 text-slate-500"}`}>
              {(o.count ?? 0).toLocaleString()}
            </span>
          </button>
        );
      })}
      {(more > 0 || all) && list.length > max && (
        <button type="button" data-testid={`${testid}-more`} onClick={() => setAll(!all)}
          className="min-h-[44px] rounded-full px-3 text-sm font-semibold text-[#B01E28] hover:underline">
          {all ? "Fewer" : `+ ${more} more`}
        </button>
      )}
    </div>
  );
}

export function ChipSection({ label, hint, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
        {label}{hint && <span className="ml-1.5 normal-case tracking-normal font-normal text-slate-400">· {hint}</span>}
      </div>
      {children}
    </div>
  );
}

// Years tapped as 5-year spans ("2015–2019"), each with the sum of its years' counts — so the number is exactly what
// the span returns.
export function yearSpans(years) {
  const by = {};
  (years || []).forEach((y) => {
    const n = Number(y.year ?? y.value);
    if (!n) return;
    const start = n - (n % 5);
    by[start] = (by[start] || 0) + (y.count || 0);
  });
  return Object.keys(by).map(Number).sort((a, b) => b - a)
    .map((s) => ({ value: `${s}-${s + 4}`, label: `${s}–${s + 4}`, count: by[s], from: String(s), to: String(s + 4) }));
}

export function slug(v) {
  return String(v ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "x";
}
